import {Suspense, useState} from 'react'

import {Button, Label, Link as PrimerLink, PageHeader} from '@primer/react'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import {PullRequestBanners} from './PullRequestBanners'
import {PullRequestHeaderSummary} from './PullRequestHeaderSummary'
import {PullRequestEditTitleForm} from './PullRequestEditTitleForm'
import {PullRequestStateLabel} from './PullRequestStateLabel'
import type {HeaderPageData} from '../page-data/payloads/header'
import {PullRequestHeaderNavigation} from './PullRequestHeaderNavigation'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useHeaderLiveUpdates} from '../hooks/use-header-live-updates'
import {PullRequestCodeButton} from './PullRequestCodeButton'
import {DiffStat} from './DiffStat'
import {clsx} from 'clsx'
import styles from './PullRequestHeader.module.css'
import responsiveStyles from './responsive.module.css'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'
import {toggleGenericFeaturePath} from '@github-ui/paths'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {isLoggedIn} from '@github-ui/client-env'
import type {NavigationUrls} from '../types/navigation-urls-types'
import {ssrSafeLocation} from '@github-ui/ssr-utils'

export type PullRequestHeaderProps = HeaderPageData & {
  isFilesRoute?: boolean
}

/**
 * LivePullRequestHeader is a thin wrapper around PullRequestHeader that will automatically update the header when the
 * alive channel is triggered with `{title_updated: true}`. This lives here instead of the base page so that
 * it doesn't re-render the entire page when the title is updated.
 */
export function LivePullRequestHeader({
  aliveChannel,
  bannersData,
  isFilesRoute,
  pullRequest,
  repository,
  urls,
  user,
}: PullRequestHeaderProps & {aliveChannel: string; isFilesRoute?: boolean}) {
  const {data: headerData} = useHeaderPageData({aliveChannel, repository, pullRequest, bannersData, urls, user})
  useHeaderLiveUpdates(aliveChannel)

  return <PullRequestHeader {...headerData} isFilesRoute={isFilesRoute} />
}

export function PullRequestHeader({
  bannersData,
  isFilesRoute,
  pullRequest,
  repository,
  urls,
  user,
}: PullRequestHeaderProps) {
  const [isEditing, setIsEditing] = useState(false)

  const shouldShowLoggedInOptions = isLoggedIn()

  const titleActions = (
    <>
      {isFilesRoute && <FilesChangedFeedback repository={repository} urls={urls} />}
      {shouldShowLoggedInOptions && (
        <>
          {user.canEditTitle && (
            <Button onClick={() => setIsEditing(true)} size="small">
              Edit
            </Button>
          )}
          <PullRequestCodeButton
            codespacesEnabled={repository.codespacesEnabled}
            headBranch={pullRequest.headBranch}
            isEnterprise={repository.isEnterprise}
            pullRequestNumber={pullRequest.number}
            repository={repository}
          />
        </>
      )}
    </>
  )

  return (
    <>
      {isEditing && (
        <PullRequestEditTitleForm
          initialTitle={pullRequest.title}
          pullRequestNumber={pullRequest.number}
          onCloseForm={() => setIsEditing(false)}
        />
      )}
      {/* This is a hack to make the title actions appear above the title on mobile */}
      {!isEditing && (
        <div
          className={clsx(
            responsiveStyles['hide-on-desktop'],
            'pb-2 mb-3 flex-md-order-1 flex-shrink-0 d-flex flex-items-center gap-1',
          )}
        >
          {titleActions}
        </div>
      )}
      <PageHeader className="flex-items-center">
        {isEditing && (
          // this is done so that we still have an h1 element present when editing the title
          <h1 className="sr-only">{`${pullRequest.title} - #${pullRequest.number}`}</h1>
        )}
        {!isEditing && (
          <>
            <PageHeader.TitleArea>
              <PageHeader.Title as="h1" className="lh-condensed">
                <VerifiedHTMLText className="f1 text-normal markdown-title" html={pullRequest.titleHtml} />
                <span className="pl-2 fgColor-muted f1-light d-inline">#{pullRequest.number}</span>
              </PageHeader.Title>
            </PageHeader.TitleArea>
            <PageHeader.Actions className={clsx(responsiveStyles['hide-on-mobile'], 'flex-items-center gap-1')}>
              {titleActions}
            </PageHeader.Actions>
          </>
        )}
        <PageHeader.Description className="d-flex flex-column flex-items-start">
          <div className="d-flex flex-column flex-sm-row gap-2 width-full flex-items-start flex-justify-between">
            <PullRequestStateLabel state={pullRequest.state} />
            <div className="flex-1">
              <PullRequestHeaderSummary
                author={pullRequest.author}
                mergedTime={pullRequest.mergedTime}
                baseBranch={pullRequest.baseBranch}
                baseRepositoryDefaultBranch={repository.defaultBranch}
                baseRepositoryName={repository.name}
                baseRepositoryOwnerLogin={repository.ownerLogin}
                canChangeBase={user.canChangeBase}
                commitsCount={pullRequest.commitsCount}
                headBranch={pullRequest.headBranch}
                headRepositoryOwnerLogin={pullRequest.headRepositoryOwnerLogin}
                headRepositoryName={pullRequest.headRepositoryName}
                isInAdvisoryRepo={pullRequest.isInAdvisoryRepo}
                isEditing={isEditing}
                mergedBy={pullRequest.mergedBy}
                mergedByName={pullRequest.mergedByName}
                repositoryId={repository.id}
                pullRequestId={pullRequest.id}
                pullRequestNumber={pullRequest.number}
                setIsEditing={setIsEditing}
                state={pullRequest.state}
              />
            </div>
          </div>
          <PullRequestBanners bannersData={bannersData} pullRequest={pullRequest} repository={repository} />
        </PageHeader.Description>
        <PageHeader.Navigation className="pt-3 px-3 ml-n3 mr-n3">
          <div className={clsx(styles.diffStatesWrapper, 'float-right d-none d-md-block')}>
            <Suspense fallback={<LoadingSkeleton width="80px" />}>
              <DiffStat />
            </Suspense>
          </div>
          <div className="flex-auto">
            <PullRequestHeaderNavigation pullRequest={pullRequest} repository={repository} urls={urls} />
          </div>
        </PageHeader.Navigation>
      </PageHeader>
    </>
  )
}

function FilesChangedFeedback({repository, urls}: {repository: HeaderPageData['repository']; urls: NavigationUrls}) {
  const onClick = async () => {
    const formData = new FormData()
    formData.append('feature_name', 'prx_files')

    await verifiedFetch(toggleGenericFeaturePath({repo: repository}), {
      body: formData,
      method: 'POST',
    })
  }

  const shouldShowLoggedInOptions = isLoggedIn()

  const pathname = ssrSafeLocation.pathname ?? ''
  const search = ssrSafeLocation.search ?? ''
  let switchBackUrl: string
  if (pathname) {
    switchBackUrl = `${pathname}${search ? `${search}&` : '?'}new_files_changed=false`
  } else {
    // Fallback to NavigationUrls for SSR or when location is unavailable
    switchBackUrl = `${urls.files}?new_files_changed=false`
  }

  return (
    <div className="d-flex flex-items-center gap-1 flex-order-2 flex-md-order-none pl-2 pl-md-0 pr-2">
      <Label variant="success" className={styles.PreviewLabel}>
        Preview
      </Label>
      {shouldShowLoggedInOptions && (
        <PrimerLink href={switchBackUrl} onClick={onClick} className="no-wrap f6 text-normal">
          Switch back
        </PrimerLink>
      )}
      <span className="f5">&middot;</span>
      <PrimerLink
        href="https://gh.io/new-files-changed-feedback"
        target="_blank"
        rel="noopener noreferrer"
        className="no-wrap f6 text-normal"
      >
        Feedback
      </PrimerLink>
    </div>
  )
}

try{ LivePullRequestHeader.displayName ||= 'LivePullRequestHeader' } catch {}
try{ PullRequestHeader.displayName ||= 'PullRequestHeader' } catch {}
try{ FilesChangedFeedback.displayName ||= 'FilesChangedFeedback' } catch {}